import { useCallback, useRef } from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { useIsRenderedFromWebView } from '@crehana/react-hooks/use-is-rendered-from-webview';

import { MyCoursesWithRules } from '@/shared/components';
import type { TUserEnrollment as TOldUserEnrollment } from '@/shared/components/MyCoursesWithRules/hooks/useCourseEnrollments/types';
import { LazyMyCoursesWithFilters } from '@/shared/components/my-courses-with-filters';
import type { TUserEnrollment } from '@/shared/components/my-courses-with-filters/hooks/use-course-enrollments/types';
import Breadcrumb from '@/views/LearnDashboard/components/Breadcrumb';
import SectionTitle from '@/views/LearnDashboard/components/SectionTitle';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';
import { useCrehanaTranslation } from '@/views/LearnDashboard/hooks';

import { LEARN_DASHBOARD_PAGE_NAME } from '../../../constants';

const CoursesSection: React.FC = () => {
  const { logEvent } = useAmplitude();
  const { t } = useCrehanaTranslation();
  const { isPlayroomEnabled, me } = useLearnDashboardContext();
  const isFromApps = useIsRenderedFromWebView();
  const sentAmplitudeEventRender = useRef(false);
  const {
    flagValue: enableNewContentFilters,
    loading: loadingNewContentFilters,
  } = useFeatureFlag('ENABLE_HOME_USER_B2B_MY_COURSES_NEW_FILTERS');

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const sendAmplitudeEventRenderRedesign = useCallback(
    (courses: (TUserEnrollment | TOldUserEnrollment)[] = []) => {
      // Note: We only want to send the event once per page load. Not on every re-render, refetch, change pagination, etc.
      if (sentAmplitudeEventRender.current) {
        return;
      }

      const eventProperties: Readonly<Record<string, number | string>> = {
        ...getSourceEventParams(),
        ...getCommonAmplitudeProperties(me, isPlayroomEnabled),
        'Courses Enrolled Count': courses.length,
        'Quiz Certification Available': 'Yes',
      };

      logEvent({
        event: 'Render Home Courses Enrolled',
        data: eventProperties,
      });

      sentAmplitudeEventRender.current = true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPlayroomEnabled, me],
  );

  if (loadingNewContentFilters) {
    return null;
  }

  return (
    <>
      {!isFromApps && (
        <Breadcrumb
          items={[{ label: t('LABEL_SIDEBAR_ITEM_MY_COURSES_BREADCRUMB') }]}
        />
      )}
      <SectionTitle title={t('LABEL_SIDEBAR_ITEM_MY_COURSES_TITLE')} />

      {enableNewContentFilters ? (
        <LazyMyCoursesWithFilters
          me={me}
          onCoursesChange={sendAmplitudeEventRenderRedesign}
          amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
        />
      ) : (
        <MyCoursesWithRules
          me={me}
          onCoursesChange={sendAmplitudeEventRenderRedesign}
          amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
        />
      )}
    </>
  );
};

export default CoursesSection;
